import React from "react";
import { FieldProps, FieldConfig, Field } from "formik";
import { isBrowser } from "@montana/helpers/browser.helper";

import { formContext } from "../form.context";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type IFieldProps<TProps> = Pick<FieldConfig, "name" | "validate"> & TProps & { defaultValue?: any };


function FormField<TProps>(props: React.PropsWithChildren<IFieldProps<TProps>>): JSX.Element | null {

    if (!isBrowser) {
        return null;
    }

    const { defaultValue = "", ...fieldProps } = props;

    const [isRegistered, setIsRegistered] = React.useState<boolean>(false);

    const { registerField, unRegisterField } = React.useContext(formContext);

    React.useEffect((): () => void => {
        registerField(props.name, defaultValue);
        setIsRegistered(true);

        return (): void => unRegisterField(props.name);
    }, []);

    return isRegistered ? <Field {...fieldProps} defaultValue={undefined} /> : null;

}

/**
 * @author SBF
 *
 * @description
 * Use to map form values onto custom form field components.
 *
 * @param { ComponentType } CustomField component to use as form field
 * @param { Function } fieldMapper function for mapping form values onto the props model of CustomField component type.
 * @param { any } [defaultValue] value to use as default, if not specified.
 *
 * @example
 *  interface IProps { ... }
 *
 *  const Component: React.FC<Props> = props => { ... }
 *
 *  const FormComponent = withForm(
 *      Component,
 *      (field, form) => ({ someBool: !!field.value, onSelect: value => form.setFieldValue(field.name, value) })
 *  )
 */
export function withForm<TComponentProps, K extends keyof TComponentProps>(
    CustomField: React.ComponentType<TComponentProps>,
    fieldMapper: (field: FieldProps<{}>["field"], form: FieldProps<{}>["form"]) => { [P in K]: TComponentProps[P] },
): React.FC<IFieldProps<Omit<TComponentProps, K>>> {
    return (props): JSX.Element => (
        // eslint-disable-next-line react/prop-types
        <FormField {...props}>
            {({ field, form }: FieldProps<{}>): JSX.Element => {
                // eslint-disable-next-line react/prop-types,@typescript-eslint/no-unused-vars
                const { validate, defaultValue: _, ...fieldProps } = props;
                const mappedProps = fieldMapper(field, form);

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                return <CustomField {...fieldProps as any} {...mappedProps} />;
            }}
        </FormField>
    );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isFieldTouched = (name: string, form: FieldProps<{}>["form"]): boolean => !(!(form.touched as any)[name] && form.submitCount === 0);



export function isFieldValid(name: string, form: FieldProps<{}>["form"]): boolean {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (!isFieldTouched(name, form)) {
        return true;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return !(form.errors as any)[name];
}
